import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  ThemeProvider,
} from '@mui/material';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import AuthLayout, { AuthLink } from './components/AuthLayout';
import { theme } from './theme';

export default function ResetPassword(_props: { theme?: any }) {
  const { t } = useTranslation('auth');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMsg(null);

    if (!password || !confirmPassword) {
      setErrorMsg(t('resetPassword.errorAllFields'));
      return;
    }
    if (password.length < 8) {
      setErrorMsg(t('resetPassword.errorPasswordLength'));
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg(t('resetPassword.errorPasswordMatch'));
      return;
    }

    setLoading(true);
    const params = Object.fromEntries(
      new URLSearchParams(window.location.search).entries()
    );

    if (!params.token) {
      setErrorMsg(t('resetPassword.errorMissingToken'));
      setLoading(false);
      return;
    }

    try {
      const decoded: JwtPayload = jwtDecode(params.token);
      if (decoded.exp && new Date(decoded.exp * 1000) < new Date()) {
        setErrorMsg(t('resetPassword.errorExpired'));
        setLoading(false);
        return;
      }
    } catch {
      setErrorMsg(t('resetPassword.errorInvalid'));
      setLoading(false);
      return;
    }

    fetch(`${process.env.REACT_APP_API}/auth/password/reset`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.token}`,
      },
      body: JSON.stringify({ password }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.statusCode && data.statusCode > 201)
          throw new Error(data.message || 'Reset failed');
        window.location.href = '/login';
      })
      .catch((e) => {
        setErrorMsg(e.message || t('resetPassword.errorGeneric'));
        setLoading(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout
        title={t('resetPassword.title')}
        subtitle={t('resetPassword.subtitle')}
        footerLeft={
          <span style={{ color: '#8B95A8' }}>
            {t('resetPassword.needNewLink')}{' '}
            <AuthLink href="/password/reset/request">{t('resetPassword.requestOne')}</AuthLink>
          </span>
        }
      >
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label={t('resetPassword.newPassword')}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2.5 }}
          />
          <TextField
            fullWidth
            label={t('resetPassword.confirmPassword')}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: '#0A0E1A' }} />
            ) : (
              t('resetPassword.updatePassword')
            )}
          </Button>
        </Box>
      </AuthLayout>
    </ThemeProvider>
  );
}
